import type { StaticImageData } from 'next/image';
import { getTopBrandUrl } from '@util/urlHelpers';
import type { Brand } from '@util/types/brands';
import BrandsMarquee from './BrandsMarquee';

import sonyLogo from 'src/i/brands/Sony.webp';
import canonLogo from 'src/i/brands/Canon.webp';
import nikonLogo from 'src/i/brands/Nikon.webp';
import fujifilmLogo from 'src/i/brands/Fujifilm.webp';
import panasonicLogo from 'src/i/brands/Panasonic.webp';
import leicaLogo from 'src/i/brands/Leica.webp';
import sigmaLogo from 'src/i/brands/Sigma.webp';
import arriLogo from 'src/i/brands/ARRI.webp';
import redLogo from 'src/i/brands/RED.webp';
import djiLogo from 'src/i/brands/Dji.webp';
import goproLogo from 'src/i/brands/GoPro.webp';

// Recognisable camera/cinema brands that have a logo asset. Each links to its
// real brand page (getTopBrandUrl → /brands/<slug>), not a keyword search.
const LOGOS: { name: Brand; logo: StaticImageData }[] = [
  { name: 'Sony', logo: sonyLogo },
  { name: 'Canon', logo: canonLogo },
  { name: 'Nikon', logo: nikonLogo },
  { name: 'Fujifilm', logo: fujifilmLogo },
  { name: 'Panasonic', logo: panasonicLogo },
  { name: 'Leica', logo: leicaLogo },
  { name: 'Sigma', logo: sigmaLogo },
  { name: 'ARRI', logo: arriLogo },
  { name: 'RED', logo: redLogo },
  { name: 'DJI', logo: djiLogo },
  { name: 'GoPro', logo: goproLogo },
];

// Alphabetical, and carrying each logo's intrinsic width/height so the marquee
// can reserve space (no pop-in gap when a logo scrolls into view).
const brands = [...LOGOS]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(({ name, logo }) => ({
    name,
    src: logo.src,
    width: logo.width,
    height: logo.height,
    href: getTopBrandUrl(name),
  }));

/**
 * "Trusted gear from the brands you shoot with" — a small centered heading over
 * a scrolling row of colour brand logos (grayscale on hover) that link to each
 * brand's page. Replaces the legacy logo-grid "Top Brands" section.
 */
const TrustedBrands = () => (
  // Below sm: pt-[40px]/pb-[40px] are now the SOLE source of the grid-to-
  // reviews-to-brands and brands-to-categories gaps (Bill, 2026-08-22 — was
  // pb-[24px], relying on the popular-categories container's own gap-[1rem]
  // (16px) to make up an even 40px. That assumed a sibling — the auctions
  // carousel — sat above popular-categories to absorb the flex gap; with no
  // live auctions, popular-categories is that flex-col's FIRST child, so a
  // CSS `gap` never applies before it and the 16px never landed — this
  // measured 24px in production against 40px for the other two gaps).
  //
  // Above sm: pt/pb-[32px] match the existing grid-to-reviews gap
  // (mt-[32px] in FeaturedListings) for the same reason — the desktop
  // reviews-to-brands gap stacked with FeaturedListings' own sm:pb-[64px] to
  // measure 104px, and brands-to-categories stacked with index.tsx's own
  // sm:mt-[4rem] to measure 92px, both against a 32px grid-to-reviews gap.
  // Both of those outer paddings are now removed so this is the sole source
  // at every gap.
  <section aria-label="Trusted brands" className="pt-[40px] pb-[40px] sm:pt-[32px] sm:pb-[32px]">
    <h2 className="mb-[20px] text-center text-[12px] font-semibold uppercase tracking-[1.2px] text-gray-400">
      Trusted gear from the brands you shoot with
    </h2>
    <BrandsMarquee brands={brands} />
  </section>
);

export default TrustedBrands;
